/* External */
import {
  memo, useMemo, useCallback, useEffect,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

/* Other */
import { getJsonImage } from 'src/theme/images';

/* Theme Assets */
import logoDgoTappp from '../../assets/svg/logos-dgo-tappp.svg';
import imageStepOne from '../../assets/images/interactivity-image-01.png';
import imageStepTwo from '../../assets/images/interactivity-image-02.png';
import iconInteractivity from '../../assets/svg/icon-interactivity.svg';

/* Components */
import Modal from '../Modal';
import ContentModal from './ContentModal';

/* Utils */
import {
  TAPPP_MODAL_STEPS_MESSAGES,
  TAPPP_MODAL_VIEW_STORAGED,
  SHOW_TAPPP_MODAL_STORAGED,
} from '../../utils/constants';
import withInteractiveFocusable from '../../hocs/withInteractiveFocusable.hoc';
import { Direction } from '../../utils/spatialNavigation';
import {
  getProfileSelected,
  getProfilesAlreadyViewedTapppModal,
} from '../../utils/profiles';

/* Other */
import { hideModal, showModal } from '../../storage/modal';
import { selectShowModal } from '../../storage/modal/selectors';

/* Styles */
import './TapppModal.scss';

function TapppModal() {
  const dispatch = useDispatch();
  const isModalShown = useSelector(selectShowModal);
  const profileSelected = useMemo(getProfileSelected, []);
  const profilesAlreadySeenModal = useMemo(getProfilesAlreadyViewedTapppModal, []);
  const profileStoraged = profilesAlreadySeenModal.find(
    (profile) => profile.profile === profileSelected.id,
  );
  const steps = Object.keys(TAPPP_MODAL_STEPS_MESSAGES);
  const [currentStep, setCurrentStep] = useState(steps[0]);
  const assets = {
    stepOne: {
      image: getJsonImage('interactivity-image-01') || imageStepOne,
      icon: getJsonImage('icon-interactivity') || iconInteractivity,
    },
    stepTwo: {
      image: getJsonImage('interactivity-image-02') || imageStepTwo,
      icon: undefined,
    },
  };

  const PROPERTY_TYPES = useMemo(() => ({
    viewedModal: 'viewedModal',
    dismissedModal: 'dismissedModal',
  }), []);

  const storeProfilesAndCloseModal = useCallback((profiles) => {
    localStorage.setItem(TAPPP_MODAL_VIEW_STORAGED, profiles);
    localStorage.setItem(SHOW_TAPPP_MODAL_STORAGED, JSON.stringify(false));
    dispatch(hideModal());
  }, [dispatch]);

  const getNewProfilesAlreadySeenModal = useCallback((propertyToChange) => {
    const newValue = propertyToChange === PROPERTY_TYPES.viewedModal
      ? true : (profileStoraged?.dismissedModal ?? 0) + 1;

    return JSON.stringify(
      profilesAlreadySeenModal.map((profile) => {
        if (profile.profile === profileSelected.id) {
          return {
            ...profileStoraged,
            [PROPERTY_TYPES[propertyToChange]]: newValue,
          };
        }

        return profile;
      }),
    );
  }, [profilesAlreadySeenModal, PROPERTY_TYPES, profileSelected.id, profileStoraged]);

  const addNewprofileToProfilesAlreadySeenModal = useCallback((profile) => {
    const newProfilesAlreadySeenModal = JSON.stringify(
      [...profilesAlreadySeenModal, profile],
    );

    storeProfilesAndCloseModal(newProfilesAlreadySeenModal);
  }, [profilesAlreadySeenModal, storeProfilesAndCloseModal]);

  // when user views all steps and closes the modal
  const handleCloseModal = useCallback(() => {
    if (profileStoraged) {
      storeProfilesAndCloseModal(getNewProfilesAlreadySeenModal(PROPERTY_TYPES.viewedModal));
    } else {
      const profile = {
        profile: profileSelected?.id,
        dismissedModal: 0,
        viewedModal: true,
      };

      addNewprofileToProfilesAlreadySeenModal(profile);
    }
  }, [
    profileSelected,
    profileStoraged,
    PROPERTY_TYPES,
    addNewprofileToProfilesAlreadySeenModal,
    getNewProfilesAlreadySeenModal,
    storeProfilesAndCloseModal,
  ]);

  const handleNextStep = useCallback(() => {
    setCurrentStep((prevState) => {
      const indexCurrentStep = steps.indexOf(prevState);

      if (indexCurrentStep < steps.length - 1) {
        return steps[indexCurrentStep + 1];
      }

      return prevState;
    });
  }, [steps]);

  const handlePreviousStep = useCallback(() => {
    setCurrentStep((prevState) => {
      const indexCurrentStep = steps.indexOf(prevState);

      if (indexCurrentStep > 0) {
        return steps[indexCurrentStep - 1];
      }

      return prevState;
    });
  }, [steps]);

  // when user presses back button
  const handleOnBack = useCallback(() => {
    if (profileStoraged) {
      storeProfilesAndCloseModal(getNewProfilesAlreadySeenModal(PROPERTY_TYPES.dismissedModal));
    } else {
      const profile = {
        profile: profileSelected?.id,
        dismissedModal: 1,
        viewedModal: false,
      };

      addNewprofileToProfilesAlreadySeenModal(profile);
    }
  }, [
    profileSelected,
    profileStoraged,
    PROPERTY_TYPES,
    addNewprofileToProfilesAlreadySeenModal,
    getNewProfilesAlreadySeenModal,
    storeProfilesAndCloseModal,
  ]);

  // when user presses right or left buttons
  const handleOnArrowPress = useCallback((direction) => {
    if (direction === Direction.RIGHT) {
      handleNextStep();
    } else if (direction === Direction.LEFT) {
      handlePreviousStep();
    }
  }, [handleNextStep, handlePreviousStep]);

  const onEnterPress = currentStep === steps[steps.length - 1] ? handleCloseModal : handleNextStep;
  const { title, description, textButton } = TAPPP_MODAL_STEPS_MESSAGES[currentStep] || {};
  const { image, icon } = assets[currentStep] || {};

  useEffect(() => {
    dispatch(showModal());
  }, [dispatch]);

  return (
    <Modal
      isModalShown={isModalShown}
      backgroundOpacity
    >
      <ContentModal
        logo={getJsonImage('logos-dgo-tappp') || logoDgoTappp}
        image={image}
        icon={icon}
        steps={steps}
        currentStep={currentStep}
        title={title}
        description={description}
        textButton={textButton}
        isModalShown={isModalShown}
        onEnterPress={onEnterPress}
        handleOnBack={handleOnBack}
        onArrowPress={handleOnArrowPress}
      />
    </Modal>
  );
}

export default memo(withInteractiveFocusable()(memo(TapppModal)));
